import { Check } from 'lucide-react';
import type { ToolCallData } from '../../standalone/types';
import { cn } from '../../utils';
import { useT } from '../../i18n';
import styles from './ThinkingTimeline.module.css';

export interface ThinkingTimelineProps {
  /** Ordered tool calls observed in the active stream. Last item is active when its status === 'calling'. */
  toolCallHistory: ToolCallData[];
  /** Header copy shown above the timeline */
  label?: string;
  /** When true, render the bouncing dots next to the label */
  isActive?: boolean;
  /** Optional logo image to display in the avatar slot */
  logoUrl?: string;
}

function stepText(tc: ToolCallData): string {
  return tc.executionMessage || tc.displayName || tc.toolName || '…';
}

const DEFAULT_LOGO_URL =
  'https://iwjbgyrjrayialdsfobn.supabase.co/storage/v1/object/public/assets/pleiful-logo.png';

export function ThinkingTimeline({
  toolCallHistory,
  label,
  isActive = true,
  logoUrl,
}: ThinkingTimelineProps) {
  const t = useT();
  const logoSource = logoUrl || DEFAULT_LOGO_URL;

  return (
    <div className={styles.wrap}>
      <div className={styles.header}>
        <img
          src={logoSource}
          alt={t('common.logoAlt')}
          className={styles.avatar}
          draggable={false}
        />
        <div className={styles.label}>
          {label ?? t('thinking.analyzing')}
          {isActive && (
            <span className={styles.dots} aria-hidden="true">
              <span />
              <span />
              <span />
            </span>
          )}
        </div>
      </div>
      {toolCallHistory.length > 0 && (
        <div className={styles.steps}>
          {toolCallHistory.map((tc, index) => {
            const isCallActive = tc.status === 'calling';
            return (
              <div
                key={`${tc.toolName}-${index}`}
                className={cn(
                  styles.step,
                  isCallActive ? styles.stepActive : styles.stepDone,
                )}
              >
                <div className={styles.check}>
                  {!isCallActive && <Check size={11} strokeWidth={3} aria-hidden="true" />}
                </div>
                <span>{stepText(tc)}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
